import { Card, Collapse, List, ListItem, ListItemSecondaryAction, ListItemText } from '@material-ui/core';
import React, { FC, useState } from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import { Button, IconButton, TextField, makeStyles } from '@material-ui/core';
import CardGiftcardIcon from '@material-ui/icons/CardGiftcard';
import { useApplyGiftCardOnPackageMutation } from './graphql/applyGiftCardOnPackage.generated';
import { useRemoveGiftCardFromPackageMutation } from './graphql/removeGiftCardFromPackage.generated';
import { useGetValidGiftCardLazyQuery } from './graphql/getValidGiftCard.generated';
import { useGetUsedGiftCardQuery } from './graphql/getUsedGiftCard.generated';
import { formatMoney } from '../../../../utils/utils';
import { useUserDetails } from '../../../../UserContext/UserContex';

interface Props {
  entityId: string;
}

const PackageGiftCard: FC<Props> = ({ entityId }) => {
  const [code, setCode] = useState<string>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const { data: giftCardData, loading: giftCardLoading } = useGetUsedGiftCardQuery({
    variables: { id: entityId, productType: 'package' }
  });
  const existingGiftCard = giftCardData && giftCardData.getUsedGiftCard;
  const exisitingGiftCardCode = existingGiftCard ? existingGiftCard.code : '';
  const { permissions } = useUserDetails();
  const canHandlePayments = permissions.canHandlePayments;

  const [removeGiftCard] = useRemoveGiftCardFromPackageMutation({
    variables: { id: entityId, code: exisitingGiftCardCode },
    refetchQueries: ['getPackage', 'getUsedGiftCard']
  });

  const [fetchGiftCard, { data, called }] = useGetValidGiftCardLazyQuery();

  const [applyGiftCard] = useApplyGiftCardOnPackageMutation({ refetchQueries: ['getPackage', 'getUsedGiftCard'] });

  const handleClose = () => setOpenModal(false);

  const onSearch = () => {
    if (code) {
      fetchGiftCard({ variables: { code } });
    }
  };

  const handleApplyGiftCard = async (code: string) => {
    const result = await applyGiftCard({ variables: { id: entityId, code } });
    handleClose();
    console.log({ result });
  };

  const handleRemove = async () => {
    await removeGiftCard();
  };

  const searchResult = data && data.getValidGiftCard;

  const renderNoResultMessage = () => {
    if (called && !searchResult) {
      return (
        <div
          style={{ color: 'grey', marginBottom: '0.5rem' }}
        >{`There is no valid gift voucher with a code ${code}. Please check if it is paid or not assigned to a different product.`}</div>
      );
    }
  };

  const renderGiftCardSection = () => {
    if (giftCardLoading) {
      return 'Loading...';
    }

    if (existingGiftCard) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ marginRight: '0.5rem' }}>{`Applied code: ${existingGiftCard.code}`}</div>
          {canHandlePayments && (
            <IconButton onClick={handleRemove} aria-label="delete">
              <DeleteIcon />
            </IconButton>
          )}
        </div>
      );
    }

    if (openModal || !canHandlePayments) {
      return null;
    }

    return (
      <Button variant="outlined" size="small" color="primary" startIcon={<CardGiftcardIcon />} onClick={() => setOpenModal(true)}>
        Apply
      </Button>
    );
  };

  if (!existingGiftCard && !canHandlePayments) {
    return null;
  }

  return (
    <Card className="border mt-8 border-gray-300 px-3 py-2 flex flex-col">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="text-lg font-bold">Gift voucher</h2>
        {renderGiftCardSection()}
      </div>

      <Collapse in={openModal} timeout="auto" unmountOnExit>
        <div className="mt-2">
          <TextField
            label="Search by code"
            variant="outlined"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{ marginBottom: '1rem', width: '100%' }}
          />
          {renderNoResultMessage()}
          <div style={{ marginBottom: '0.5rem' }}>
            <Button
              style={{ marginRight: '0.5rem' }}
              color="primary"
              disabled={code ? !code.length : true}
              onClick={onSearch}
              variant="contained"
              size="small"
            >
              Search
            </Button>
            <Button size="small" color="default" onClick={handleClose} variant="contained">
              Cancel
            </Button>
          </div>

          {searchResult ? (
            <List>
              <ListItem>
                <ListItemText>{formatMoney(searchResult.amount)}</ListItemText>
                <ListItemSecondaryAction>
                  <Button variant="outlined" onClick={() => handleApplyGiftCard(searchResult.code)}>
                    Apply
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          ) : null}
        </div>
      </Collapse>
    </Card>
  );
};

export default PackageGiftCard;
